import { createClient } from 'graphql-ws';
import { useEffect } from 'react';
import { proxy, ref } from 'valtio';
import { useProxySnapshot, useStableCallback, useStableValue } from './utils.js';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
function GraphQLReactWS({
  graphqlWsOptions
}) {
  const client = typeof window === "undefined" ? null : createClient(graphqlWsOptions);
  class ListenerGenerator {
    constructor({ cleanup }) {
      __publicField(this, "done", false);
      __publicField(this, "throwMe", null);
      __publicField(this, "pending", []);
      __publicField(this, "cleanup");
      __publicField(this, "deferred", null);
      this.cleanup = cleanup;
    }
    dispose() {
      var _a;
      if (this.done)
        return;
      this.done = true;
      (_a = this.cleanup) == null ? void 0 : _a.call(this);
      this.cleanup = null;
    }
    resolveNext(result) {
      var _a;
      if (this.done)
        return;
      this.pending.push(result);
      (_a = this.deferred) == null ? void 0 : _a.resolve(false);
    }
    resolveCompleted() {
      var _a;
      if (this.done)
        return;
      this.done = true;
      (_a = this.deferred) == null ? void 0 : _a.resolve(true);
      this.dispose();
    }
    reject(error) {
      var _a;
      if (this.done)
        return;
      this.throwMe = error;
      (_a = this.deferred) == null ? void 0 : _a.reject(error);
      this.dispose();
    }
    // To make the listener a valid async generator
    [Symbol.asyncIterator]() {
      return this;
    }
    async next() {
      if (this.done)
        return { done: true, value: void 0 };
      if (this.throwMe)
        throw this.throwMe;
      if (this.pending.length)
        return { value: this.pending.shift(), done: false };
      const isDone = await new Promise(
        (resolve, reject) => this.deferred = { resolve, reject }
      );
      if (isDone)
        return { done: true, value: void 0 };
      return { value: this.pending.shift(), done: false };
    }
    async throw(error) {
      throw error;
    }
    async return() {
      this.dispose();
      return { value: void 0, done: true };
    }
  }
  class BroadcastAsyncGenerator {
    constructor({
      generator,
      cleanupBroadcaster
    }) {
      __publicField(this, "generator");
      __publicField(this, "listeners", /* @__PURE__ */ new Set());
      __publicField(this, "cleanupBroadcaster");
      __publicField(this, "disposed", false);
      this.generator = generator;
      this.cleanupBroadcaster = cleanupBroadcaster;
      this.broadcast().catch(console.error);
    }
    async broadcast() {
      try {
        for await (const value of this.generator) {
          for (const listener of this.listeners) {
            listener.resolveNext(value);
          }
        }
        for (const listener of this.listeners) {
          listener.resolveCompleted();
        }
      } catch (err) {
        for (const listener of this.listeners) {
          listener.reject(err);
        }
      }
    }
    removeListener(listener) {
      this.listeners.delete(listener);
      if (this.listeners.size === 0)
        this.cleanupBroadcaster();
    }
    subscribe(params) {
      if (this.disposed)
        throw Error("Generator has already been disposed!");
      const cleanupListener = () => {
        listener.resolveCompleted();
        this.removeListener(listener);
        params.abortController.abort();
      };
      const listener = new ListenerGenerator({
        cleanup: cleanupListener
      });
      params.abortSignal.addEventListener("abort", cleanupListener);
      this.listeners.add(listener);
      return listener;
    }
  }
  function graphqlWsSubscribe({
    payload,
    onDispose
  }) {
    if (!client)
      throw Error("graphql-ws client not available");
    const { query, variables } = payload;
    function handleDispose() {
      dispose();
      onDispose();
    }
    const generator = new ListenerGenerator({
      cleanup: handleDispose
    });
    const dispose = client.subscribe(payload, {
      next: (result) => {
        generator.resolveNext(result);
        const effects = effectsStore[query];
        if (effects && result.data) {
          for (const effect of effects) {
            try {
              Promise.all([
                effect({
                  operation: query,
                  result: {
                    ...result,
                    data: result.data
                  },
                  variables
                })
              ]).catch(() => null);
            } catch (err) {
            }
          }
        }
      },
      error: (err) => {
        generator.reject(err);
        cleanupGenerator();
      },
      complete: () => {
        generator.resolveCompleted();
        cleanupGenerator();
      }
    });
    function cleanupGenerator() {
      handleDispose();
      generator.resolveCompleted();
    }
    const broadcaster = new BroadcastAsyncGenerator({
      generator,
      cleanupBroadcaster: cleanupGenerator
    });
    return {
      subscribe(params) {
        return broadcaster.subscribe(params);
      },
      cleanup: cleanupGenerator
    };
  }
  const effectsStore = {};
  const Effects = {
    /**
     * Add an effect callback to be called every time the specified operation request has been completed
     *
     * It returns a callback that's going to stop the effect from being called
     *
     * @example
     * Effects.onCompleted(TestQuery, ({ operation, result: { data }, variables }) => {
     *  console.log({
     *    operation,
     *    data,
     *    variables
     *  });
     * });
     */
    onCompleted(operation, callback) {
      const effects = effectsStore[operation] || (effectsStore[operation] = /* @__PURE__ */ new Set());
      effects.add(callback);
      return function removeEffect() {
        effects.delete(callback);
        if (effects.size === 0)
          effectsStore[operation] = null;
      };
    }
  };
  const storeChannels = /* @__PURE__ */ new Map();
  function subscribe({
    query,
    variables
  }, subscription) {
    if (!client)
      return null;
    const payload = {
      query,
      variables
    };
    const payloadKey = JSON.stringify(payload);
    let channel;
    const existingChannel = storeChannels.get(payloadKey);
    if (existingChannel) {
      channel = existingChannel;
    } else {
      const subscriptionsControllers = /* @__PURE__ */ new Set();
      channel = {
        subscription: graphqlWsSubscribe({
          payload,
          onDispose() {
            storeChannels.delete(payloadKey);
            for (const controller of subscriptionsControllers) {
              controller.abort();
            }
            subscriptionsControllers.clear();
          }
        }),
        subscriptionsControllers
      };
      storeChannels.set(payloadKey, channel);
    }
    const {
      subscriptionsControllers: channelSubscriptionsControllers,
      subscription: { cleanup: channelCleanup, subscribe: subscribe2 }
    } = channel;
    const abortController = new AbortController();
    const abortSignal = abortController.signal;
    function cleanupSubscriptionController() {
      abortController.signal.removeEventListener("abort", cleanupSubscriptionController);
      channelSubscriptionsControllers.delete(abortController);
      if (channelSubscriptionsControllers.size === 0)
        channelCleanup();
    }
    abortController.signal.addEventListener("abort", cleanupSubscriptionController);
    channelSubscriptionsControllers.add(abortController);
    const subscriptionValue = subscription({
      iterator: subscribe2({
        abortSignal,
        abortController
      }),
      abortController,
      abortSignal
    }).catch((err) => {
      console.error(err);
      return null;
    });
    subscriptionValue.finally(() => abortController.abort());
    return { subscription: subscriptionValue, abortController, abortSignal };
  }
  const subscriptionStores = /* @__PURE__ */ new Map();
  function getSubscriptionStore({
    query,
    variables,
    initialData = null
  }) {
    const storeKey = query + JSON.stringify(variables);
    const existingStore = subscriptionStores.get(storeKey);
    if (existingStore)
      return existingStore;
    const newStore = proxy({
      data: initialData,
      error: null,
      ref: ref({
        current: initialData
      })
    });
    subscriptionStores.set(storeKey, newStore);
    return newStore;
  }
  function useSubscription({
    query,
    onData,
    onError,
    variables,
    initialData = null,
    enabled = true
  }) {
    const store = getSubscriptionStore({
      query,
      // Can't verify the conditional types around optional variables
      variables,
      initialData
    });
    if (initialData && !store.data) {
      store.data = initialData;
      store.ref.current = initialData;
    }
    const { data, error } = useProxySnapshot(store);
    const onDataCallback = useStableCallback((resultWithData) => {
      if (!onData)
        return;
      try {
        Promise.all([onData(resultWithData)]).catch(console.error);
      } catch (err) {
        console.error(err);
      }
    });
    const onErrorCallback = useStableCallback((resultWithError) => {
      if (!onError)
        return;
      try {
        Promise.all([onError(resultWithError)]).catch(console.error);
      } catch (err) {
        console.error(err);
      }
    });
    const stableVariables = useStableValue(variables);
    useEffect(() => {
      if (typeof window === "undefined" || stableVariables === false || enabled === false)
        return;
      const subscription = subscribe(
        {
          query,
          // Can't verify the conditional types around optional variables
          variables: stableVariables
        },
        async function({ iterator }) {
          for await (const result of iterator) {
            if (result.data) {
              const resultWithData = {
                ...result,
                data: result.data
              };
              if (store.ref.current !== result) {
                store.data = resultWithData;
                if (!result.errors && store.error) {
                  store.error = null;
                }
              }
              onDataCallback(resultWithData);
            }
            if (result.errors) {
              const resultWithError = {
                ...result,
                errors: result.errors
              };
              if (store.ref.current !== result)
                store.error = resultWithError;
              onErrorCallback(resultWithError);
            }
            store.ref.current = result;
          }
        }
      );
      if (!subscription)
        return;
      return () => {
        subscription.abortController.abort();
      };
    }, [stableVariables, enabled, query]);
    return {
      data,
      error,
      store
    };
  }
  function setSubscriptionData({
    query,
    variables
  }, data) {
    const store = getSubscriptionStore({
      query,
      // Can't verify the conditional types around optional variables
      variables,
      initialData: data
    });
    if (store.ref.current !== data) {
      store.data = data;
      store.ref.current = data;
    }
  }
  return {
    client,
    subscribe,
    useSubscription,
    subscriptionStores,
    setSubscriptionData,
    getSubscriptionStore,
    Effects
  };
}

export { GraphQLReactWS };
